import { useEffect, useState } from 'react';
import { breakpoints } from '../configs/breakpoints';
import { DesktopOS, getDesktopOS } from '../utils/getDesktopOS';
import {
	DeviceType,
	type DeviceTypeKeys,
	getDeviceType,
} from '../utils/getDeviceType';
import { useBreakpoint } from './useBreakpoint';

const scaleDesktop = (
	relativeWidth: number,
	responsiveWidth: number = window.innerWidth,
) => {
	const scaledSize = responsiveWidth / relativeWidth;
	document.documentElement.style.setProperty(
		'--scale',
		scaledSize.toString(),
	);
};

const clearScale = () => {
	document.documentElement.style.setProperty('--scale', '1');
};

const scaleDesktopS = () => {
	scaleDesktop(breakpoints.desktopM);
};

const scaleDesktopM = () => {
	scaleDesktop(breakpoints.desktopXL);
};

const scaleDesktopXL = () => {
	scaleDesktop(breakpoints.desktopXL);
};
const scaleDesktopXXL = () => {
	scaleDesktop(breakpoints.desktopXXL, breakpoints.desktopXXL / 0.8);
};
const scaleOverflowedDesktop = () => {
	scaleDesktop(breakpoints.desktopXL);
};

const vhCalculate = () => {
	const vh = `${window.innerHeight * 0.01}px`;
	document.documentElement.style.setProperty('--vh', `${vh}`);
};
const clearVh = () => {
	document.documentElement.style.removeProperty('--vh');
};

export const useDeviceInitializer = () => {
	const [deviceInitialized, setDeviceInitialized] = useState(false);
	const { desktopS, desktopM, desktopXL, desktopXXL, tablet, mobile } =
		useBreakpoint();

	const deviceType = getDeviceType();
	const desktopOS = getDesktopOS();

	useEffect(() => {
		if ((tablet || mobile) && deviceType === 'mobile') {
			if (tablet) setDeviceType('tablet');
			if (mobile) setDeviceType('mobile');
			vhCalculate();
			window.addEventListener('resize', vhCalculate);
			clearScale();
		} else {
			setDeviceType('desktop');
			clearVh();
			if (desktopOS !== DesktopOS.macos) {
				document.documentElement.setAttribute(
					'withCustomScrollbar',
					'',
				);
			}
		}

		return () => {
			window.removeEventListener('resize', vhCalculate);
		};
	}, [desktopOS, deviceType, tablet, mobile]);

	useEffect(() => {
		if (deviceType === DeviceType.desktop) {
			if (desktopS) {
				scaleDesktopS();
				window.addEventListener('resize', scaleDesktopS);
			} else {
				window.removeEventListener('resize', scaleDesktopS);
			}

			if (desktopM) {
				scaleDesktopM();
				window.addEventListener('resize', scaleDesktopM);
			} else {
				window.removeEventListener('resize', scaleDesktopM);
			}

			if (desktopXL) {
				scaleDesktopXL();
				window.addEventListener('resize', scaleDesktopXL);
			} else {
				window.removeEventListener('resize', scaleDesktopXL);
			}
			if (desktopXXL) {
				scaleDesktopXXL();
				window.addEventListener('resize', scaleDesktopXXL);
			} else {
				window.removeEventListener('resize', scaleDesktopXXL);
			}

			if (!desktopXXL) {
				scaleOverflowedDesktop();
			}
		}

		return () => {
			window.removeEventListener('resize', scaleDesktopS);
			window.removeEventListener('resize', scaleDesktopM);
			window.removeEventListener('resize', scaleDesktopXL);
			window.removeEventListener('resize', scaleDesktopXXL);
		};
	}, [desktopS, desktopM, desktopXL, desktopXXL, deviceType]);

	return deviceInitialized;
	function setDeviceType(deviceType: DeviceTypeKeys) {
		document.documentElement.setAttribute('device', deviceType);
		setDeviceInitialized(true);
	}
};
